<template>
  <template v-if="authStore.user || route.name !== INFO_DETAILS">
    <AppSidebar v-model:open="showSidebar" @open-inbox="onOpenInbox" />
    <div class="layout-container safe-top">
      <div class="glow"></div>
      <header class="header" :class="{ home }">
        <Badge :label="totalCounter">
          <Button
            size="large"
            type="secondary"
            icon="menu-1"
            icon-only
            @click="showSidebar = true"
            data-qa="open-menu-button"
          />
        </Badge>
        <h1 v-if="!isTablet && !home" class="title text-xl m-0 ml-5" data-qa="page-title">
          {{ title }}
        </h1>
      </header>
      <div
        class="content mobile-scroll-container"
        :class="[isWide ? 'p-0' : 'md:max-w-sm px-5 md:px-0', { 'home-content': home }]"
      >
        <div v-if="!isWide && isTablet" class="title w-full text-4xl pt-10 pb-5">
          {{ title }}
        </div>
        <slot />
      </div>
    </div>
    <InboxDialog v-model:open="showInbox" v-if="isPatient && showInbox" />
  </template>
  <slot v-else />
</template>
<script setup lang="ts">
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import Badge from '@/components/common/badge/Badge.vue'
import Button from '@/components/common/buttons/Button.vue'
import { useScreens } from '@/composables/useScreens'
import { useUserRoles } from '@/composables/useUserRoles'
import { APPOINTMENTS } from '@/modules/appointments/routes'
import { useAuthStore } from '@/modules/auth/store'
import { useCounterStore } from '@/modules/counter/store'
import { HISTORY } from '@/modules/history/routes'
import { HOME } from '@/modules/home/routes'
import InboxDialog from '@/modules/inbox/components/InboxDialog.vue'
import { INFO_DETAILS } from '@/modules/info/routes'

import AppSidebar from '../components/page/AppSidebar.vue'

const route = useRoute()
const showSidebar = ref(false)
const showInbox = ref(false)
const { isTablet } = useScreens()
const { t } = useI18n()
const store = useCounterStore()
const authStore = useAuthStore()
const { isPatient } = useUserRoles()

const title = computed(() => t(`page-titles.${String(route.name)}`))

const totalCounter = computed(() => {
  if (!store.counter) {
    return 0
  }
  return Object.values(store.counter).reduce(
    (acc: number, val) => acc + (isNaN(Number(val)) ? +!!val : Number(val)),
    0
  )
})

const isWide = computed(() => [HOME, APPOINTMENTS, HISTORY].includes(route.name as string))
const home = computed(() => route.name === HOME)

const onOpenInbox = () => {
  showSidebar.value = false
  showInbox.value = true
}

onMounted(async () => {
  if (!Capacitor.isNativePlatform()) return

  PushNotifications.requestPermissions().then((result) => {
    if (result.receive === 'granted') {
      PushNotifications.register()
    } else {
      localStorage.setItem('fcmToken', '')
    }
  })
})

watch(
  () => route.query?.message_id,
  (messageId) => {
    if (messageId) {
      showInbox.value = true
    }
  },
  { immediate: true }
)
</script>

<style scoped lang="postcss">
.layout-container {
  @apply w-full h-full bg-base-gradient flex flex-col relative md:items-center;

  .glow {
    background-image: url('@/assets/background-glow.png');
    background-size: cover;
    @apply absolute z-0 top-0 right-0 w-[390px] h-[401px] md:w-[602px] md:h-[555px] pointer-events-none;
  }

  .header {
    @apply p-5 flex items-center border-b border-gray-900-10 md:border-none w-full;
    &.home {
      @apply border-b-0 relative z-10;
      background-color: transparent !important;
      background: transparent !important;
    }
  }

  .title {
    @apply text-accent-500 font-bold capitalize;
  }

  .content {
    @apply flex-grow flex flex-col items-center z-[1] w-full overflow-y-auto;

    &.home-content {
      margin-top: -5rem;
      padding-top: 5rem;
      mask-image: linear-gradient(
        to bottom,
        transparent 0%,
        black calc(env(safe-area-inset-top) + 5rem),
        black 100%
      );
      -webkit-mask-image: linear-gradient(
        to bottom,
        transparent 0%,
        black calc(env(safe-area-inset-top) + 5rem),
        black 100%
      );
    }
  }

  .mobile-scroll-container::-webkit-scrollbar {
    display: none;
  }
}
</style>
